/**
 * Session resumption storage — idb-keyval (IndexedDB).
 * I1 fix (2026-05-09): implements Phase 9 stub with idb-keyval so sessions
 * survive tab close, Safari background eviction, and shared-device interruptions.
 *
 * Key: `nudge:session:{userId}`
 * All reads/writes are best-effort — any IDB error is swallowed so a storage
 * failure never interrupts the quiz experience.
 */

import { get, set, del } from 'idb-keyval'

export interface ActiveSession {
  subject:   string
  topic:     string
  mode:      'practice' | 'quiz'
  savedAt:   string   // ISO 8601
}

function sessionKey(userId: string): string {
  return `nudge:session:${userId}`
}

/**
 * Returns a resumable session for this user, or null if none exists.
 * Falls back to null on any IDB error (private-browsing mode, quota exceeded, etc.).
 */
export async function findActiveSession(
  userId: string
): Promise<{ session: ActiveSession } | null> {
  try {
    const stored = await get<ActiveSession>(sessionKey(userId))
    if (!stored) return null
    return { session: stored }
  } catch {
    return null
  }
}

/**
 * Persists the current session state for later resumption.
 * Best-effort — errors are swallowed.
 */
export async function saveActiveSession(
  userId:  string,
  session: ActiveSession
): Promise<void> {
  try {
    await set(sessionKey(userId), session)
  } catch {
    // IDB unavailable (private-browsing, quota, etc.) — continue without persistence
  }
}

/**
 * Clears the saved session (called after session completes or user declines resume).
 * Best-effort — errors are swallowed.
 */
export async function clearActiveSession(userId: string): Promise<void> {
  try {
    await del(sessionKey(userId))
  } catch {
    // best-effort
  }
}
